import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useUser} from './UserContext';
import styles from '../designs/MathPage.module.css';// Використовуємо стилі від MatchPage для консистентності
import {enableDemoMode} from '../utils/demoAccess';

function AccessRequestPage() {
    const {user} = useUser();
    const navigate = useNavigate();
    const [code, setCode] = useState('');
    const [wasInvited, setWasInvited] = useState(false);
    const [referrerTelegramId, setReferrerTelegramId] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const canUseReferralInvite = useMemo(() => {
        const totalBets = Number(user?.total_bets || 0);
        return !user?.first_access_at && totalBets <= 0;
    }, [user?.first_access_at, user?.total_bets]);

    useEffect(() => {
        if (!canUseReferralInvite && wasInvited) {
            setWasInvited(false);
            setReferrerTelegramId('');
        }
    }, [canUseReferralInvite, wasInvited]);

    const handleSubmit = async () => {
        const cleanCode = String(code || '').trim();
        const cleanReferrerId = String(referrerTelegramId || '').trim();
        if (!cleanCode) {
            setMessage('Будь ласка, введи свій 1win ID');
            return;
        }
        const shouldSendReferral = canUseReferralInvite && wasInvited;
        if (shouldSendReferral && !cleanReferrerId) {
            setMessage('Введи Telegram ID друга, який тебе запросив');
            return;
        }
        if (shouldSendReferral && !/^\d+$/.test(cleanReferrerId)) {
            setMessage('Telegram ID друга має складатися тільки з цифр');
            return;
        }
        if (!user || !user.telegram_id) {
            setMessage('Помилка: користувач не авторизований');
            return;
        }

        setIsLoading(true);
        try {
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await fetch(`${API_BASE}/request-access`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0',
                },
                body: JSON.stringify({
                    telegram_id: user.telegram_id,
                    code: cleanCode,
                    referrer_telegram_id: shouldSendReferral ? Number(cleanReferrerId) : null,
                }),
            });
            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.detail || 'Помилка відправки запиту');
            }
            const data = await res.json();
            setMessage(data.message);
            // Перенаправляємо на головну сторінку після успішного запиту
            setTimeout(() => navigate('/'), 2000);
        } catch (error) {
            setMessage('Помилка: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTryFree = () => {
        enableDemoMode();
        navigate('/');
    };

    const handleContactAdmin = () => {
        const text = encodeURIComponent('Хочу вступити в Сім’ю, щоб отримати доступ до бота. Підкажи, будь ласка, що потрібно зробити.');
        const url = `https://t.me/grach_admin?text=${text}`;
        const tg = window.Telegram?.WebApp;
        if (tg?.openTelegramLink) {
            tg.openTelegramLink(url);
            return;
        }
        window.location.href = url;
    };

    return (
        <div className={`${styles.pageContainer} ${styles.accessGatePage}`}>
            <div className={styles.accessGateShell}>
                <div className={styles.accessGateBrand}>
                    <span>СІМ’Я</span>
                    <strong>ГРАЧА</strong>
                    <small>закритий футбольний бот</small>
                </div>

                <div className={styles.accessGatePanel}>
                    <div className={styles.accessGateHeader}>
                        <span>Доступ</span>
                        <h1>Вступи в Сім’ю</h1>
                        <p>Повний бот відкривається після доступу. Без доступу можна один раз спробувати урізаний режим.</p>
                    </div>

                    <button
                        type="button"
                        onClick={handleContactAdmin}
                        className={styles.accessGateAdmin}
                    >
                        Написати grach_admin
                    </button>

                    <button
                        type="button"
                        onClick={handleTryFree}
                        className={styles.accessGatePrimary}
                    >
                        Спробувати безкоштовно
                    </button>

                    <div className={styles.accessGateDivider}>або надішли заявку через 1win ID</div>

                    <label className={styles.accessGateFieldLabel}>
                        <span>Твій 1win ID</span>
                        <small>Це ID акаунта 1win, не Telegram ID. По ньому адмін перевірить доступ вручну.</small>
                    </label>
                    <input
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="Наприклад: 950213166"
                        className={styles.accessGateInput}
                        disabled={isLoading}
                    />

                    {canUseReferralInvite && (
                        <>
                            <label className={styles.accessGateCheck}>
                                <input
                                    type="checkbox"
                                    checked={wasInvited}
                                    onChange={(e) => setWasInvited(e.target.checked)}
                                    disabled={isLoading}
                                />
                                <span>
                                    <strong>Мене запросив друг</strong>
                                    <small>Якщо друг вже у Сім’ї, він зможе отримати 1500 балів після ручної перевірки депу.</small>
                                </span>
                            </label>

                            {wasInvited && (
                                <div className={styles.accessGateReferral}>
                                    <label className={styles.accessGateFieldLabel}>
                                        <span>Telegram ID друга</span>
                                        <small>Введи саме цифровий TG ID друга, не @username.</small>
                                    </label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={referrerTelegramId}
                                        onChange={(e) => setReferrerTelegramId(e.target.value.replace(/[^\d]/g, ''))}
                                        placeholder="Наприклад: 1078623102"
                                        className={styles.accessGateInput}
                                        disabled={isLoading}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    <button
                        onClick={handleSubmit}
                        className={styles.accessGateSecondary}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Перевіряю...' : 'Надіслати заявку'}
                    </button>

                    <p className={styles.accessGateNote}>
                        1win ID піде адміну на ручну перевірку. Після підтвердження доступ відкриється автоматично.
                    </p>
                </div>
            </div>
            {message && (
                <div className={`${styles.infoMessage} ${styles.accessGateMessage}`}>
                    <strong>Інформація:</strong>
                    <pre className={styles.formattedText}>{message}</pre>
                </div>
            )}
        </div>
    );
}

export default AccessRequestPage;
